import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, CheckCircle2, Loader2, Mail } from 'lucide-react';
import { Button } from './Button';
import { useAuth } from '../../contexts/AuthContext';
import { subscriptionService } from '../../services';

interface EmailVerificationPanelProps {
  className?: string;
}

/**
 * Actionable "verify your email to unlock" state for a paid but unverified
 * buyer. The backend refuses paid access with 403 EMAIL_VERIFICATION_REQUIRED
 * and hides paid exams, so this replaces the silent empty/locked state.
 *
 * The resend action calls the dedicated resend endpoint, which sends the same
 * mail the buyer originally needed: the combined setup mail for a pay-first
 * account with no password, or a plain verification mail for an account that
 * already has one. It no longer reuses the password-reset route, so the mail
 * the buyer receives matches its copy.
 */
export const EmailVerificationPanel: React.FC<EmailVerificationPanelProps> = ({ className }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [kind, setKind] = useState<'setup' | 'verification'>('verification');

  const handleResend = async () => {
    if (!user?.email) return;
    setStatus('sending');
    try {
      const result = await subscriptionService.resendPassSetup();
      setKind(result.kind);
      setStatus('sent');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className={`rounded-2xl border border-amber-200 bg-amber-50 p-6 ${className ?? ''}`}>
      <div className="flex items-start gap-4">
        <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-amber-100">
          <Mail className="h-5 w-5 text-amber-700" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-bold text-amber-900">{t('emailVerification.title')}</h2>
          <p className="mt-1 text-sm text-amber-800">{t('emailVerification.description')}</p>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button
              variant="primary"
              onClick={handleResend}
              loading={status === 'sending'}
              disabled={status === 'sending' || !user?.email}
              icon={<Mail className="h-4 w-4" />}
            >
              {status === 'sending' ? t('emailVerification.resending') : t('emailVerification.resend')}
            </Button>
          </div>

          {status === 'sent' && (
            <p className="mt-3 flex items-center gap-2 text-sm text-success">
              <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
              {t('emailVerification.resent')}
            </p>
          )}
          {status === 'error' && (
            <p className="mt-3 flex items-center gap-2 text-sm text-error">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {t('emailVerification.resendError')}
            </p>
          )}
          {status === 'sent' && (
            <p className="mt-1 text-xs text-amber-700">
              {kind === 'setup'
                ? t('emailVerification.resentHintSetup')
                : t('emailVerification.resentHintVerify')}
            </p>
          )}
          {status === 'sending' && (
            <p className="mt-3 flex items-center gap-2 text-xs text-amber-700">
              <Loader2 className="h-3 w-3 animate-spin" />
              {t('emailVerification.resendingHint')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationPanel;
